import { ApolloQueryResult, useQuery } from '@apollo/client';
import {
  ActionPlanningActionPlanActionPlanFragment,
  ActionPlanningActionPlanGetActionPlanQuery,
} from '@predium/client-graphql';
import isNil from 'lodash/isNil';
import { useSnackbar } from 'notistack';
import { PropsWithChildren, createContext, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingScreen from '../../../../components/LoadingScreen';
import { createStandardErrorSnackbar } from '../../../../components/NotistackProvider';
import { GET_ACTIONPLAN } from '../../../../graphql/ActionPlanning.queries';
import { PATHS } from '../../../../routes';

type ActionPlanContextProps = {
  actionPlan: ActionPlanningActionPlanActionPlanFragment;
  reloadCurrentActionPlan: () => Promise<ApolloQueryResult<ActionPlanningActionPlanGetActionPlanQuery>>;
};

const ActionPlanContext = createContext<ActionPlanContextProps | null>(null);

type Props = PropsWithChildren<{}>;

function ActionPlanContextProvider({ children }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { stableId, scenarioId } = useParams();
  const navigate = useNavigate();

  const {
    data,
    loading,
    error,
    refetch: reloadCurrentActionPlan,
  } = useQuery(GET_ACTIONPLAN, {
    variables: { actionPlanStableId: parseInt(stableId!) },
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (isNil(error)) return;
    const isNotFound = error.graphQLErrors.some((e) => e.extensions?.code === 'ACTION_PLAN_NOT_FOUND');
    createStandardErrorSnackbar()(enqueueSnackbar);
    if (isNotFound) {
      navigate(PATHS.actionPlanning.scenario({ scenarioId: Number(scenarioId) }));
    }
  }, [error, enqueueSnackbar, navigate, scenarioId]);

  if (loading && isNil(data)) return <LoadingScreen />;
  if (isNil(data)) return null;
  const actionPlan = data.getActionPlanByStableId.action_plan;
  if (isNil(actionPlan)) return null;

  return (
    <ActionPlanContextInner actionPlan={actionPlan} reloadCurrentActionPlan={reloadCurrentActionPlan}>
      {children}
    </ActionPlanContextInner>
  );
}

function ActionPlanContextInner({
  actionPlan,
  reloadCurrentActionPlan,
  children,
}: PropsWithChildren<ActionPlanContextProps>) {
  const value = useMemo(() => ({ actionPlan, reloadCurrentActionPlan }), [actionPlan, reloadCurrentActionPlan]);

  return <ActionPlanContext.Provider value={value}>{children}</ActionPlanContext.Provider>;
}

export { ActionPlanContext, ActionPlanContextProvider };
